/* ==========================================================================
   GYM STUDIO — the gym builder
   ==========================================================================
   Design rule that drives everything here: THE USER NEVER SEES AN EMPTY ROOM.
   Every version of a builder that fails does so by handing someone a blank
   rectangle and asking them to do the work. This one lands furnished, and every
   interaction afterwards is a refinement — swap, remove, nudge — not
   construction.

   Sections below:
     1. Catalog        — load + normalise the product feed
     2. Classification — what KIND of machine is this, and what shape is it
     3. Geometry       — real footprints, clearance, and packing a room
     4. Recommendation — which machines go in, in what order
     5. Render         — the plan and the tray
     6. Interaction    — swap, remove, drag
     7. Money          — tiers, totals, add to cart
     8. State          — save + resume
   ========================================================================== */
(function () {
  'use strict';

  /* Everything Shopify says can actually be bought right now. Deliberately NOT
     the "All" collection: its `inventory > 0` rule drops 92 sellable BUILT
     machines, because untracked stock reads as 0. See the endpoint template. */
  var FEED = '/search?q=*&type=product&view=builder-data';
  /* BUMP THIS WHENEVER THE FEED'S SHAPE OR CONTENTS CHANGE.

     The catalogue is cached in sessionStorage, which is the right call — it is
     a few hundred products and re-fetching it on every navigation is wasteful.
     But the key was fixed, so a shopper with a tab already open kept serving
     themselves the old catalogue no matter what shipped. That bit during this
     very change: the feed had gained the free-weight sets and recovered seven
     products, the browser had them on a plain fetch, and the builder still
     showed the old 320 because it never asked again.

     v3: free-weight variant expansion, plus the pdp- collection exemption.
     v4: rows carry `s` (variant sku) for the SKU-pinned art/footprint tier. */
  var CACHE_KEY = 'ctxGymCatalog.v4';

  /* Dillon's tiers. Thresholds are on the pre-discount subtotal — the number
     the shopper is watching climb. Kept in one place because the discount
     FUNCTION hardcodes the same numbers server-side and the two must agree;
     see the note in the section file. */
  var TIERS = [
    { min: 12000000, pct: 15 },
    { min: 6000000,  pct: 10 },
    { min: 0,        pct: 5  }
  ];

  function tierFor(cents) {
    for (var i = 0; i < TIERS.length; i++) if (cents >= TIERS[i].min) return TIERS[i];
    return TIERS[TIERS.length - 1];
  }
  function nextTier(cents) {
    var best = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (TIERS[i].min > cents && (!best || TIERS[i].min < best.min)) best = TIERS[i];
    }
    return best;
  }

  /* ── 1. CATALOG ────────────────────────────────────────────────────────── */

  /* SKU-pinned footprints outrank the metafields. CTX_GYM_ART_SKU (declared by
     the section) maps variant sku -> [artUrl, length_in, width_in]; a null url
     with real numbers is a dims-only entry. Two reasons this tier exists:
       - a run of BUILT listings still shares a placeholder 87x44 in the
         metafields, and the pack measured the real machines;
       - the dumbbell SETS are variants of one product, so they can only ever
         inherit the product's single set of metafield dims — but a 5-100lb set
         is physically TWO 85in racks and a 5-150 is two 3-tier racks. The set
         rows get their true rack-combination footprints here, anchored on the
         store's own measured 85x16 two-tier saddle rack.
     Applied on every boot, cache hit or fresh fetch, so a map update never
     waits on a sessionStorage expiry. */
  function patchSkuDims(list) {
    var map = window.CTX_GYM_ART_SKU || {};
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      var e = p && p.s ? map[p.s] : null;
      if (!e) continue;
      var L = Number(e[1]), W = Number(e[2]);
      if (L > 0 && W > 0) { p.l = L; p.w = W; }
    }
    return list;
  }

  function loadCatalog() {
    try {
      var hit = sessionStorage.getItem(CACHE_KEY);
      if (hit) return Promise.resolve(patchSkuDims(JSON.parse(hit)));
    } catch (e) { /* private mode */ }

    /* NO `Accept: application/json` HEADER — it breaks this.
       Shopify content-negotiates collection URLs: send that header and it
       answers with its OWN collection JSON payload ({collection:{...}}) instead
       of rendering the alternate template, so `?view=` is silently ignored and
       the response looks superficially fine. Fetch it plain and parse the text
       ourselves. */
    function grab(url) {
      return fetch(url).then(function (r) { return r.text(); }).then(function (t) { return JSON.parse(t); });
    }

    return grab(FEED)
      .then(function (first) {
        var rest = [];
        for (var p = 2; p <= first.pages; p++) {
          rest.push(grab(FEED + '&page=' + p).catch(function () { return { products: [] }; }));
        }
        return Promise.all(rest).then(function (pages) {
          var out = first.products.slice();
          pages.forEach(function (pg) { out = out.concat(pg.products || []); });
          /* Cache the RAW feed; the sku patch runs after the cache read on
             every boot, so an updated map reaches shoppers immediately. */
          try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(out)); } catch (e) {}
          return patchSkuDims(out);
        });
      });
  }

  /* ── 2. CLASSIFICATION ─────────────────────────────────────────────────────
     The old builder carried NO equipment type at all — nothing distinguished a
     treadmill from a leg press, which is precisely why it drew generic grey
     rectangles and felt fake. Class is what earns a real silhouette. */

  /* The model library IS the class table — label, group, clearance and aspect
     all live beside the drawing so a new machine type is one entry, not four
     edits in four files. */
  var CLASSES = window.CTXGymModels;

  /* Free weights and small accessories ride in the tray and never take floor
     space — Dillon's call. A plan cluttered with dumbbell pairs is noise. */
  var TRAY_ONLY = { freeweight: true };

  /* custom.product_type is a controlled 41-value vocabulary on this store, so it
     is matched FIRST and exactly. Title matching is only a fallback for the
     handful of products whose type is blank — titles are the least reliable
     signal and will happily call a "Smith Press" a Smith machine when it is a
     plate-loaded chest press. */
  var BY_TYPE = {
    'treadmill': 'treadmill',
    'total body elliptical': 'elliptical', 'lower body elliptical': 'elliptical', 'elliptical': 'elliptical',
    'arc trainers': 'arc', 'adaptive motion trainers': 'arc',
    'upright bikes': 'upright', 'group cycling': 'spin',
    'recumbent bikes': 'recumbent', 'recumbent steppers': 'recumbent',
    'air bikes': 'airbike',
    'rowers': 'rower',
    'steppers': 'stepper',
    'stretch trainers': 'stretch',
    'functional trainers': 'functional',
    'smith machine': 'smith',

    /* Dillon: "one for a selectorized legs, one for plate loaded legs" — the
       muscle-group types ARE the model granularity, so each gets its own
       drawing rather than sharing a generic box with a different label. */
    'legs selectorized': 'sel_legs', 'back selectorized': 'sel_back',
    'chest selectorized': 'sel_chest', 'shoulders selectorized': 'sel_shoulders',
    'arms selectorized': 'sel_arms', 'abdominal selectorized': 'sel_abs',
    'legs plate-loaded': 'pl_legs', 'back plate-loaded': 'pl_back',
    'chest plate-loaded': 'pl_chest', 'shoulders plate-loaded': 'pl_shoulders',
    'arms plate-loaded': 'pl_arms',
    'power rack': 'rack',
    'weight racks': 'storage',
    'flat benches': 'benchflat',
    'adjustable benches': 'benchadj', 'utility & upright benches': 'benchadj',
    'specialty benches': 'benchspec',
    'ab & hyperextension benches': 'benchab',
    'dumbbells': 'freeweight', 'weight plates': 'freeweight', 'barbells & bars': 'freeweight',
    'kettlebells': 'freeweight', 'fitness accessories': 'freeweight'
  };

  function classify(p) {
    var ty = String(p.ty || '').trim().toLowerCase();

    var s = (ty + ' ' + (p.t || '')).toLowerCase();

    /* Multi-stations are drawn by STACK COUNT, which the product name states
       outright — "8 Stack Multi Station Jungle Gym", "5-Stack Multi Jungle".
       Checked before the type lookup because the type says only "All-In-One
       Units" for all three, and the difference between a 4-stack and an 8-stack
       is the single most visible thing about them on a floor plan. */
    if (/all-?in-?one|multi-?station|multi-?jungle|multi-?gym|jungle|home gym package/.test(s)) {
      /* Read the number out of the name — "8 Stack", "10 Station", "5-Stack" all
         appear. Measured: a Cybex "8 Station Multi Jungle Gym" was landing on the
         5-stack drawing because only "stack" was matched, and a Precor "Multi-Gym
         – 8 Stack / 10 Station" was not recognised as a rig at all. */
      var n = 0;
      var m = s.match(/(\d+)\s*-?\s*(?:stack|station)/);
      if (m) n = parseInt(m[1], 10);
      if (!n && /eight/.test(s)) n = 8;
      if (!n && /five/.test(s)) n = 5;
      if (!n && /four/.test(s)) n = 4;

      if (n >= 6) return 'multi8';
      if (n >= 5) return 'multi5';
      if (n >= 2) return 'multi4';
      return 'multi5';
    }

    if (BY_TYPE[ty]) return BY_TYPE[ty];

    /* Muscle group crossed with mechanism, for anything whose type string is
       shaped differently than the table above expects. */
    var mech = /plate-?loaded/.test(s) ? 'pl' : (/selectorized/.test(s) ? 'sel' : '');
    if (mech) {
      if (/\bleg|squat|calf|glute|hamstring|quad|hack\b/.test(s)) return mech + '_legs';
      if (/\bback|row|pulldown|pull-?up|lat\b/.test(s)) return mech + '_back';
      if (/chest|pec|fly|bench press/.test(s)) return mech + '_chest';
      if (/shoulder|delt|overhead/.test(s)) return mech + '_shoulders';
      if (/arm|bicep|tricep|curl|extension/.test(s)) return mech + '_arms';
      if (/ab|core|crunch|oblique/.test(s)) return mech === 'sel' ? 'sel_abs' : 'pl_arms';
      return mech === 'sel' ? 'sel_chest' : 'pl_chest';
    }
    if (/treadmill/.test(s)) return 'treadmill';
    if (/arc trainer|adaptive motion/.test(s)) return 'arc';
    if (/elliptical|ascent|cross trainer/.test(s)) return 'elliptical';
    if (/step ?mill|stair|climber|gauntlet|powermill|jacob/.test(s)) return 'stepper';
    if (/row(er|ing)/.test(s)) return 'rower';
    if (/air bike|assault/.test(s)) return 'airbike';
    if (/recumbent/.test(s)) return 'recumbent';
    if (/spin|indoor cycle|group cycling/.test(s)) return 'spin';
    if (/bike|cycle/.test(s)) return 'upright';
    if (/smith/.test(s)) return 'smith';
    if (/functional trainer|dual adjustable pulley/.test(s)) return 'functional';
    if (/power rack|half rack|squat rack|rig|cage/.test(s)) return 'rack';
    if (/dumbbell rack|plate tree|weight rack|storage/.test(s)) return 'storage';
    if (/dumbbell|kettlebell|weight plate|barbell|olympic bar|bumper|band/.test(s)) return 'freeweight';
    if (/hyperextension|ab bench|glute|roman chair/.test(s)) return 'benchab';
    if (/adjustable bench|utility bench/.test(s)) return 'benchadj';
    if (/olympic bench|incline bench|decline bench/.test(s)) return 'benchspec';
    if (/bench/.test(s)) return 'benchflat';
    if (/plate-?loaded/.test(s)) return 'plate';

    if (p.k === 'cardio') return 'upright';
    return 'selector';
  }

  /* ── 3. GEOMETRY ───────────────────────────────────────────────────────────
     Footprint in inches, plan-view: WIDTH across, DEPTH front-to-back.

     The metafields give length / width / height. Dillon's rule elsewhere is that
     width is the shortest of the three, but for a floor plan we need the two
     FLOOR dimensions specifically — height must be excluded or a tall narrow
     machine would be drawn as a monster. So: drop the height, use the other two.

     Where dimensions are missing (33 of 335, measured) we fall back to the
     footprint_sqft metafield and square it off, and flag it. Drawing a confident
     shape from a number we do not have is how the old builder lost trust. */

  function footprint(p) {
    var cls = classify(p);

    if (p.l > 0 && p.w > 0) {
      /* Longest floor dimension runs front-to-back for machines you stand or
         walk on, which is how they are actually oriented in a room. */
      var a = Math.max(p.l, p.w), b = Math.min(p.l, p.w);
      return { w: b, d: a, est: false };
    }

    /* No dimensions: derive a shape from the footprint_sqft metafield using the
       typical width:depth ratio for that CLASS, rather than squaring the area
       off. Area alone cannot tell a rower from a power rack — they can occupy
       the same square footage and look nothing alike — and a near-square blob
       is exactly what made the old builder look fake. Area is preserved; only
       the proportions are inferred, and the result is flagged `est` so the tray
       says "size estimated" instead of quietly asserting it. */
    if (p.sf > 0) {
      var aspect = (CLASSES[cls] && CLASSES[cls].aspect) || 0.7;
      var area = p.sf * 144;
      var depth = Math.sqrt(area / aspect);
      return { w: Math.round(aspect * depth), d: Math.round(depth), est: true };
    }

    var fallback = {
      treadmill: [35, 84], elliptical: [30, 80], arc: [34, 76], upright: [24, 50],
      recumbent: [26, 68], spin: [24, 50], airbike: [26, 52], stepper: [33, 58],
      rower: [24, 96], stretch: [36, 60], rack: [50, 50], storage: [62, 24],
      benchflat: [24, 55], benchadj: [26, 58], benchspec: [60, 80], benchab: [30, 58],
      sel_legs: [46, 66], sel_back: [48, 60], sel_chest: [48, 58], sel_shoulders: [46, 58],
      sel_arms: [42, 56], sel_abs: [42, 56],
      pl_legs: [52, 84], pl_back: [54, 66], pl_chest: [54, 64], pl_shoulders: [52, 62],
      pl_arms: [46, 58],
      functional: [58, 60], smith: [60, 66],
      multi4: [78, 78], multi5: [90, 90], multi8: [116, 116],
      freeweight: [36, 20]
    }[cls] || [40, 60];

    /* Free weights are one CLASS but five very different objects, so the class
       fallback is far too blunt for them: a barbell rack and a plate tree are
       not the same shape. These are the medians from Dillon's own asset sheet.

       This only bites now that free weights are placed on the floor — as tray
       items their footprint was never drawn, and several carry no dimensions at
       all, which was rendering them 0in x 0in: invisible and impossible to
       grab. */
    if (cls === 'freeweight') {
      var byType = {
        'dumbbells': [36, 16],
        'weight-plates': [18, 18],
        'barbells-and-bars': [84, 18],
        'fitness-accessories': [36, 24],
        'kettlebells': [54, 24]
      }[(window.CTXGymArt ? window.CTXGymArt.typeKey(p.ty) : '')];
      if (byType) fallback = byType;
    }

    return { w: fallback[0], d: fallback[1], est: true };
  }

  /* ── ROTATION ──────────────────────────────────────────────────────────────
     A machine at 90° occupies a footprint with width and depth swapped, and so
     does its clearance. Everything that reasons about space — packing, overlap,
     clamping to the walls — must ask for the EFFECTIVE box rather than the raw
     footprint, or a rotated treadmill will be packed as though it were still
     pointing the other way and quietly overlap its neighbour.

     Only 0 / 90 / 180 / 270 are allowed. Free rotation looks impressive and is
     useless: gyms are laid out square to the walls, and arbitrary angles make the
     overlap maths a polygon problem for no gain the shopper can feel. */
  function turned(rot) { return Math.abs((rot || 0) % 180) === 90; }

  function effFp(it) {
    var fp = it.fp;
    return turned(it.rot) ? { w: fp.d, d: fp.w, est: fp.est } : { w: fp.w, d: fp.d, est: fp.est };
  }

  /* Defensive on the class lookup. The clearance table is keyed by class, and a
     probe object built for a geometry question does not always carry one — the
     free-space search hands in a bare {fp, rot} in one path. Throwing there took
     the whole layout down rather than losing one machine's walkway, which is the
     wrong failure by a wide margin. */
  var NO_PAD = { x: 0, y: 0 };

  function effPad(it) {
    var entry = CLASSES[it && it.cls];
    var pd = (entry && entry.pad) || NO_PAD;
    return turned(it && it.rot) ? { x: pd.y, y: pd.x } : { x: pd.x, y: pd.y };
  }

  /* ── DIRECTIONAL CLEARANCE ENVELOPES ───────────────────────────────────────
     Adopted 2026-08-07 from Dillon's layout-logic guideline. The symmetric pads
     above still drive ROW PACKING (they encode shared aisles, which the
     guideline's per-face numbers do not); these envelopes drive everything that
     reasons about WHICH SIDE the space belongs on: tight-flags, placement
     preference, the safety rules, and the halo on the plan.

     f = front, b = back, s = each side, in inches. FRONT FOLLOWS THE ARTWORK:
     every model is drawn console/working-face at the TOP of its footprint, so at
     rot 0 the front points -y (up the plan) and the back points +y. Fighting the
     art with abstract facing semantics would draw halos on the wrong side of the
     machine — the drawing is the truth the shopper sees.

     Two rules of the guideline are kept verbatim:
       · envelopes MAY overlap each other (an aisle is shared space);
       · a FOOTPRINT may not sit inside a neighbour's envelope — that is what
         "tight" now means, per face, instead of a circular halo.

     Deliberate deviation: bench sides are 24, not the guideline's 30 — a bench
     lives beside a rack by design here (the pair is exempted below), and 30in
     sides flagged every home gym's bench permanently. */
  var ENV = {
    treadmill: { f: 24, b: 79, s: 20 },   /* b79 is a SAFETY spec, enforced below */
    elliptical:{ f: 24, b: 24, s: 20 },
    arc:       { f: 24, b: 24, s: 20 },
    upright:   { f: 24, b: 24, s: 20 },
    recumbent: { f: 24, b: 24, s: 20 },
    spin:      { f: 24, b: 24, s: 20 },
    airbike:   { f: 24, b: 24, s: 20 },
    stepper:   { f: 24, b: 24, s: 20 },
    rower:     { f: 20, b: 20, s: 20 },
    stretch:   { f: 24, b: 24, s: 12 },

    rack:      { f: 60, b: 6,  s: 24 },
    smith:     { f: 60, b: 6,  s: 24 },
    functional:{ f: 84, b: 0,  s: 18 },
    multi4:    { f: 36, b: 6,  s: 24 },
    multi5:    { f: 36, b: 6,  s: 24 },
    multi8:    { f: 36, b: 6,  s: 24 },

    sel_legs:  { f: 36, b: 12, s: 24 }, sel_back: { f: 36, b: 12, s: 24 },
    sel_chest: { f: 36, b: 12, s: 24 }, sel_shoulders: { f: 36, b: 12, s: 24 },
    sel_arms:  { f: 36, b: 12, s: 24 }, sel_abs: { f: 36, b: 12, s: 24 },
    pl_legs:   { f: 36, b: 12, s: 24 }, pl_back: { f: 36, b: 12, s: 24 },
    pl_chest:  { f: 36, b: 12, s: 24 }, pl_shoulders: { f: 36, b: 12, s: 24 },
    pl_arms:   { f: 36, b: 12, s: 24 },

    benchflat: { f: 24, b: 24, s: 24 }, benchadj: { f: 24, b: 24, s: 24 },
    benchspec: { f: 24, b: 24, s: 24 }, benchab: { f: 24, b: 24, s: 24 },

    storage:   { f: 48, b: 0,  s: 12 },
    freeweight:{ f: 48, b: 0,  s: 12 }
  };
  var ENV_DEFAULT = { f: 24, b: 12, s: 12 };

  /* The envelope in ROOM coordinates — {t, r, b, l} — for an item at its current
     rotation. rot 0: front up. SVG rotation is clockwise, so 90 points the front
     right, 180 down, 270 left. Only 0/90 are reachable from the UI today; the
     other two cases exist so nothing breaks if that changes. */
  function envPad(it) {
    var e = ENV[it && it.cls] || ENV_DEFAULT;
    switch (((it && it.rot) || 0) % 360) {
      case 90:  return { t: e.s, r: e.f, b: e.s, l: e.b };
      case 180: return { t: e.b, r: e.s, b: e.f, l: e.s };
      case 270: return { t: e.s, r: e.b, b: e.s, l: e.f };
      default:  return { t: e.f, r: e.s, b: e.b, l: e.s };
    }
  }

  /* The bench-and-rack pair is the guideline's one blessed overlap: the bench
     LIVES in the rack's front envelope. Flagging it would nag every correctly
     laid out home gym. */
  function envExempt(clsA, clsB) {
    var benchy = /^bench/;
    var racky = /^(rack|smith|multi4|multi5|multi8)$/;
    return (benchy.test(clsA) && racky.test(clsB)) ||
           (benchy.test(clsB) && racky.test(clsA));
  }

  /* Shelf packing. Sorted deepest-first, laid in rows across the room. Not the
     tightest possible packing, but it is stable, fast, and — crucially — always
     produces a layout a human would recognise as a gym floor rather than a
     jigsaw. Machines land in rows against the back wall forward, which is how
     real gyms are laid out. */
  /* ── Zoning ────────────────────────────────────────────────────────────────
     Real gyms are laid out in functional bands, not in whatever order the
     catalogue happened to return: cardio along the front, the selectorized
     circuit through the middle, racks and platforms at the back where the noise
     and the loading are, benches beside them, storage against the wall.

     Sorting by zone and forcing a row break between zones is enough to produce
     that read. It costs nothing in packing efficiency worth caring about and it
     is the difference between a plan that looks like a gym and one that looks
     like a shelf-stacking exercise. It also renumbers the tray by zone, so the
     list reads in the same order someone would walk the floor. */
  /* ── BANDS, REORDERED PER THE LAYOUT GUIDELINE (2026-08-07) ────────────────
     Old order ran cardio → machines → racks → benches → storage, which scattered
     the strength corner across three bands: the bench was two rows from the rack
     it serves, and the plates were on the far wall — the guideline's textbook
     "storage never goes on the far wall" failure.

     New walk of the floor, top (back wall) downward:

       0  cardio      — consoles at the back wall exactly as the art draws them,
                        which points every treadmill's 79in rear throw INTO the
                        room instead of at a wall. Safety and aesthetics agree.
       1  strength    — the anchor band: rack first (it is the anchor item),
                        then its dependents IN ADJACENCY ORDER: benches beside
                        the rack (the blessed envelope overlap), then up to two
                        storage pieces flanking within reach of the lifting
                        position. Left-packed, so the anchor lands off-centre —
                        a centred anchor splits a small room into two unusable
                        halves.
       2  machines    — the selectorized/plate-loaded circuit.
       3  free weights + any storage beyond the two flankers.

     RANK orders WITHIN a band; the tray reads in the same walk. */
  var ZONE = {
    treadmill: 0, elliptical: 0, arc: 0, upright: 0, recumbent: 0, spin: 0,
    airbike: 0, rower: 0, stepper: 0, stretch: 0,

    rack: 1, smith: 1, multi8: 1, multi5: 1, multi4: 1, functional: 1,
    benchflat: 1, benchadj: 1, benchspec: 1, benchab: 1,

    sel_legs: 2, sel_back: 2, sel_chest: 2, sel_shoulders: 2, sel_arms: 2, sel_abs: 2,
    pl_legs: 2, pl_back: 2, pl_chest: 2, pl_shoulders: 2, pl_arms: 2,

    storage: 3, freeweight: 3
  };

  var RANK = {
    rack: 0, smith: 1, multi8: 1, multi5: 1, multi4: 1, functional: 2,
    benchflat: 3, benchadj: 3, benchspec: 3, benchab: 3,
    storage: 4
  };

  function zoneOf(cls) {
    var z = ZONE[cls];
    return z === undefined ? 2 : z;
  }

  function rankOf(cls) {
    var r = RANK[cls];
    return r === undefined ? 5 : r;
  }

  /* ── PLACEMENT SEARCH ──────────────────────────────────────────────────────
     THE SHELF PACKER IS NOT A FIT TEST, AND USING IT AS ONE IS THE BUG.

     `arrange` lays machines in rows and forces a row break whenever the ZONE
     changes, so each function gets its own band across the floor. That produces
     a plan that reads like a gym, and it is worth keeping. But it means the
     depth cursor marches down once per zone whether or not the previous row was
     anywhere near full. In a 11ft x 12ft room, five machines spanning four zones
     consume four row-breaks — roughly 240in of depth in a 144in room — so the
     LAST zones (storage, free weights) were pushed past the back wall and
     flagged "will not fit" with half the floor visibly empty.

     That is the whole of the reported dumbbell-set failure. Items 1, 2 and 3 sat
     against the wall because they were early in the zone order; item 5 was a
     free-weight set, the last zone, and there was no row left for it. Nothing
     was wrong with its footprint, its artwork, its pivot or its dimensions.

     So: a genuine bounded free-space search, which answers the only question
     that matters — is there anywhere in this room this machine physically fits?
     The shelf packer still decides the PREFERRED layout; this decides whether
     something is really impossible.

     Two passes, and the order encodes the clearance semantics the brief asks
     for. Pass 0 wants a spot where the walkway is respected as well. Pass 1
     accepts a spot where the machine physically fits but a neighbour is closer
     than the recommended gap — because recommended clearance is advice, and a
     machine that fits against a wall must be allowed to sit against a wall. */
  var SEARCH_CAP = 20000;         /* candidate tests, hard ceiling */

  function boxHit(ax, ay, aw, ad, bx, by, bw, bd) {
    /* A hair of tolerance so two machines placed flush do not read as touching:
       positions are floats and 132 - 36 lands at 95.99999999999999. */
    return ax < bx + bw - 0.02 && ax + aw > bx + 0.02 &&
           ay < by + bd - 0.02 && ay + ad > by + 0.02;
  }

  /* Candidate coordinates along one axis. A plain grid march cannot express
     "flush against the far wall" unless the room happens to be a multiple of the
     step, so the two wall positions are always offered explicitly, and so is
     every existing machine's near and far edge — which is what lets a piece
     butt up against its neighbour rather than floating a few inches off it. */
  function axisCandidates(span, size, others, key, sizeKey, step) {
    var max = span - size;
    if (max < -0.02) return [];
    var out = [0];
    if (max > 0.02) out.push(max);
    for (var v = step; v < max; v += step) out.push(v);
    for (var i = 0; i < others.length; i++) {
      var o = others[i];
      var near = o[key] - size;                /* my far edge on their near edge */
      var far = o[key] + o[sizeKey];           /* my near edge on their far edge */
      if (near > 0.02 && near < max) out.push(near);
      if (far > 0.02 && far < max) out.push(far);
    }
    out.sort(function (a, b) { return a - b; });
    /* De-duplicate: the edge candidates frequently coincide with grid ones. */
    var uniq = [];
    for (var j = 0; j < out.length; j++) {
      if (!uniq.length || out[j] - uniq[uniq.length - 1] > 0.5) uniq.push(out[j]);
    }
    return uniq;
  }

  /* `others` is a list of {x, y, w, d} PHYSICAL boxes plus optional pad, already
     rotated. Built by the caller so this function never has to know about item
     shape. */
  function occupied(items) {
    return items.map(function (it) {
      var e = effFp(it);
      var pd = effPad(it);
      var env = envPad(it);
      return {
        x: it.x || 0, y: it.y || 0, w: e.w, d: e.d,
        px: pd.x, py: pd.y,
        et: env.t, er: env.r, eb: env.b, el: env.l,
        cls: it.cls
      };
    });
  }

  /* Does box (bx,by,bw,bd) sit inside a's directional envelope? The guideline's
     rule: envelopes may overlap each other, a footprint may not enter one. */
  function inEnvelope(a, bx, by, bw, bd) {
    return boxHit(a.x - a.el, a.y - a.et, a.w + a.el + a.er, a.d + a.et + a.eb,
                  bx, by, bw, bd);
  }

  /* Find a physically valid position for one item, leaving everything else
     exactly where it is. Returns {x, y, rot, tight} or null.

     `tight` means it fits but a neighbour is inside the recommended walkway —
     the caller discloses that rather than refusing the placement.

     Rotation: 0 and 90 are both tried, because a 84in-deep rower that will not
     fit a 60in-deep room across the depth fits perfectly along the width, and
     refusing to consider that is another way of declaring a false failure. The
     item's CURRENT rotation is tried first so an Add never spins a machine the
     shopper already turned. */
  function findSpot(item, others, room, keepouts, opts) {
    opts = opts || {};
    var step = opts.step || 6;
    var boxes = occupied(others);
    var kos = keepouts || [];
    var tests = 0;

    var rots = [item.rot || 0];
    if (opts.allowRotate !== false) {
      /* The OTHER orientation. Asked via `turned` rather than by arithmetic on
         the angle: a first attempt computed the alternative as
         ((rot + 90) % 180 === 0 ? 90 : 0), which for rot=0 evaluates to 0 — the
         same orientation again — so rotation was never actually tried and a 24in
         x 130in rower was rejected from a 140in x 40in room it fits along.
         Only the caught-by-test version is safe here. */
      rots.push(turned(item.rot || 0) ? 0 : 90);
      /* 180/270 occupy exactly the same box as 0/90, so testing them would
         double the work for identical geometry. */
    }

    for (var pass = 0; pass < 2; pass++) {
      for (var r = 0; r < rots.length; r++) {
        var probe = { fp: item.fp, cls: item.cls, rot: rots[r] };
        var e = effFp(probe);
        var env = envPad(probe);
        if (e.w > room.w + 0.02 || e.d > room.d + 0.02) continue;

        var xs = axisCandidates(room.w, e.w, boxes, 'x', 'w', step);
        var ys = axisCandidates(room.d, e.d, boxes, 'y', 'd', step);

        for (var yi = 0; yi < ys.length; yi++) {
          for (var xi = 0; xi < xs.length; xi++) {
            if (++tests > SEARCH_CAP) return null;
            var x = xs[xi], y = ys[yi];
            var clash = false;

            for (var b = 0; b < boxes.length && !clash; b++) {
              var o = boxes[b];
              if (pass === 0) {
                /* Envelope semantics from the layout guideline, 2026-08-07:
                   envelopes may overlap each other; a FOOTPRINT may not sit in
                   one. So the good-spot test is two asymmetric questions — is
                   my footprint in their envelope, is theirs in mine — rather
                   than the old shared-gap halo. Directional, so a spot beside a
                   treadmill passes where the same spot behind it fails. */
                if (envExempt(probe.cls, o.cls)) {
                  if (boxHit(x, y, e.w, e.d, o.x, o.y, o.w, o.d)) clash = true;
                } else if (
                  inEnvelope(o, x, y, e.w, e.d) ||
                  boxHit(x - env.l, y - env.t, e.w + env.l + env.r, e.d + env.t + env.b,
                         o.x, o.y, o.w, o.d)
                ) clash = true;
              } else {
                if (boxHit(x, y, e.w, e.d, o.x, o.y, o.w, o.d)) clash = true;
              }
            }

            /* Keepouts — door swings, columns — are HARD in both passes. A leg
               press parked in a door swing is not a clearance preference. */
            for (var k = 0; k < kos.length && !clash; k++) {
              if (!kos[k]) continue;
              if (boxHit(x, y, e.w, e.d, kos[k].x, kos[k].y, kos[k].w, kos[k].d)) clash = true;
            }

            /* ── SAFETY RULES — hard in AUTO placement, never in manual ────────
               opts.safety is set by the quiz build and the suggestions probe.
               A manual add keeps the staging philosophy: the shopper may put a
               machine anywhere and gets advice, not refusals.

               Treadmill rear: 79in is a safety spec (belt throw), not a
               preference, and it must be REAL floor — inside the room, clear of
               footprints and door swings. Checked in both passes because a
               "tight but fits" treadmill with 30in behind it is exactly the
               thing this rule exists to prevent.

               Barbell lane: a rack needs 108in of clear width along its bar
               axis (84in bar + a loaded plate each side). Envelopes don't
               capture it because the lane extends BEYOND the sides. Smith
               machines and multi-stations are exempt — their bars are captive. */
            if (!clash && opts.safety) {
              if (probe.cls === 'treadmill') {
                var rear = rearZone(x, y, e, probe.rot, 79);
                if (rear.x < -0.02 || rear.y < -0.02 ||
                    rear.x + rear.w > room.w + 0.02 || rear.y + rear.d > room.d + 0.02) clash = true;
                for (var rb = 0; rb < boxes.length && !clash; rb++) {
                  if (boxHit(rear.x, rear.y, rear.w, rear.d, boxes[rb].x, boxes[rb].y, boxes[rb].w, boxes[rb].d)) clash = true;
                }
                for (var rk = 0; rk < kos.length && !clash; rk++) {
                  if (kos[rk] && boxHit(rear.x, rear.y, rear.w, rear.d, kos[rk].x, kos[rk].y, kos[rk].w, kos[rk].d)) clash = true;
                }
              }
              if (!clash && probe.cls === 'rack') {
                /* Bar axis is the item's WIDTH axis. Lane: 108 wide, centred on
                   the rack, as deep as the rack itself. Walls are hard. */
                var lw = 108;
                var lane = turned(probe.rot)
                  ? { x: x, y: y + e.d / 2 - lw / 2, w: e.w, d: lw }
                  : { x: x + e.w / 2 - lw / 2, y: y, w: lw, d: e.d };
                if (lane.x < -0.02 || lane.y < -0.02 ||
                    lane.x + lane.w > room.w + 0.02 || lane.y + lane.d > room.d + 0.02) clash = true;
                for (var lb = 0; lb < boxes.length && !clash; lb++) {
                  if (boxHit(lane.x, lane.y, lane.w, lane.d, boxes[lb].x, boxes[lb].y, boxes[lb].w, boxes[lb].d)) clash = true;
                }
              }
            }

            if (!clash) return { x: x, y: y, rot: rots[r], tight: pass === 1 };
          }
        }
      }
    }
    return null;
  }

  /* The rectangle a treadmill's belt throws into: 79in beyond its BACK face,
     which follows the artwork — rot 0 draws the console at the top, so the rear
     projects +y. */
  function rearZone(x, y, e, rot, depth) {
    switch ((rot || 0) % 360) {
      case 90:  return { x: x - depth, y: y, w: depth, d: e.d };          /* rear = left */
      case 180: return { x: x, y: y - depth, w: e.w, d: depth };          /* rear = top */
      case 270: return { x: x + e.w, y: y, w: depth, d: e.d };            /* rear = right */
      default:  return { x: x, y: y + e.d, w: e.w, d: depth };            /* rear = bottom */
    }
  }

  /* ── THE FOUR PLACEMENT STATES ─────────────────────────────────────────────
     `fits` was one boolean carrying three different facts — outside the room, on
     top of something, or blocking a door — and the UI drew all of them the same
     red. It also could not express the fourth state at all: physically fine, but
     tighter than the recommended walkway.

     Dillon, 2026-08-06: people deliberately park a machine outside the room while
     they rearrange, and being told "this item will not fit" for doing that is
     wrong. Outside-the-room is now a STAGING state, not an error. So the states
     have to be distinguishable, because they need different words, different
     colours, and different levels of alarm:

       ok        — physical footprint inside the room, clear of everything
       tight     — physically fine; a neighbour is inside the recommended walkway.
                   Advice. Never blocks anything.
       outside   — part or all of the footprint is outside the room. Deliberate and
                   allowed while rearranging; flagged, never corrected.
       collision — overlapping another machine, or in a door's swing. The only
                   state that is genuinely wrong.

     Returned per item, in BUILD order, so the renderer and the tray can read one
     array instead of each re-deriving the geometry. */
  function placementStates(items, room, keepouts) {
    var report = fitReport(items, room, keepouts);
    var tight = clearanceReport(items, room);

    var outside = {}, clash = {}, near = {};
    report.outside.forEach(function (i) { outside[i] = true; });
    report.overlapping.concat(report.blocked).forEach(function (i) { clash[i] = true; });
    tight.forEach(function (i) { near[i] = true; });

    return items.map(function (it, i) {
      /* Collision outranks outside. A machine parked outside the room AND on top
         of another one has a real problem, and the real problem is the one worth
         naming. */
      if (clash[i]) return 'collision';
      if (outside[i]) return 'outside';
      if (near[i]) return 'tight';
      return 'ok';
    });
  }

  /* Is a neighbour inside this item's recommended walkway? Reported separately
     from fit, and never used to reject a placement — see fitReport's contract. */
  function clearanceReport(items, room) {
    /* Directional since 2026-08-07: a neighbour BESIDE a treadmill at 21in is
       fine (sides need 20); the same neighbour BEHIND it is flagged (the rear
       needs 79). The old circular halo could not tell those apart, so it both
       nagged legal layouts and blessed unsafe ones. Symmetric in blame: if j sits
       in i's envelope, both are marked — the fix is moving either one. */
    var boxes = occupied(items);
    var tight = [];
    function mark(i) { if (tight.indexOf(i) === -1) tight.push(i); }
    for (var i = 0; i < boxes.length; i++) {
      for (var j = i + 1; j < boxes.length; j++) {
        var a = boxes[i], b = boxes[j];
        if (envExempt(a.cls, b.cls)) continue;
        if (inEnvelope(a, b.x, b.y, b.w, b.d) || inEnvelope(b, a.x, a.y, a.w, a.d)) {
          mark(i); mark(j);
        }
      }
    }
    return tight;
  }

  /* ── THE SAME TWO TESTS, REPORTED AS PAIRS ─────────────────────────────────
     `fitReport().overlapping` and `clearanceReport()` both return a FLAT list of
     every index involved, which is the right shape for painting items and the
     wrong shape for writing a sentence: two machines on top of each other are
     ONE thing to tell somebody about, and a flat list says it twice.

     These return the pairs instead, for the advice panel and the printed plan.
     Deliberately the same geometry as the functions above rather than a second
     opinion — if the two ever disagree, the shopper is told about a problem the
     tool cannot see, or misses one it can. */
  function overlapPairs(items) {
    var out = [];
    for (var i = 0; i < items.length; i++) {
      for (var j = i + 1; j < items.length; j++) {
        var ea = effFp(items[i]), eb = effFp(items[j]);
        if (items[i].x < items[j].x + eb.w && items[i].x + ea.w > items[j].x &&
            items[i].y < items[j].y + eb.d && items[i].y + ea.d > items[j].y) {
          out.push([i, j]);
        }
      }
    }
    return out;
  }

  function tightPairs(items) {
    var boxes = occupied(items);
    var out = [];
    for (var i = 0; i < boxes.length; i++) {
      for (var j = i + 1; j < boxes.length; j++) {
        var a = boxes[i], b = boxes[j];
        if (envExempt(a.cls, b.cls)) continue;
        if (inEnvelope(a, b.x, b.y, b.w, b.d) || inEnvelope(b, a.x, a.y, a.w, a.d)) {
          out.push([i, j]);
        }
      }
    }
    return out;
  }

  function arrange(items, room, opts) {
    opts = opts || {};

    /* Up to two storage pieces are promoted into the strength band as FLANKERS —
       plates and dumbbells within a step of the lifting position, per the
       guideline. The rest keep their own band near the front. Assigned before
       the sort so the comparator can stay pure. */
    var flankersLeft = 2;
    items.forEach(function (it) {
      it._band = zoneOf(it.cls);
      it._rank = rankOf(it.cls);
      if (it.cls === 'storage' && flankersLeft > 0) {
        it._band = 1; it._rank = 4;
        flankersLeft--;
      }
    });

    /* Stable sort into bands, adjacency-ranked within each. `items` is
       reordered in place so the tray and the plan stay in step. */
    items.sort(function (a, b) {
      if (a._band !== b._band) return a._band - b._band;
      return a._rank - b._rank;
    });

    /* ── THE OPEN-FLOOR RESERVE ────────────────────────────────────────────────
       The guideline's step 9: open floor is a placement, not leftover space. A
       full-width strip at the FRONT of the room (bottom of the plan — nearest
       the shopper, where a real gym keeps its open training floor) is held back
       from the packing cursor. Bigger rooms reserve more.

       Soft, in this order: the cursor never packs into it, and the rescue search
       tries to respect it first — but a machine that would otherwise land in the
       apron is allowed into the reserve, because "your gym does not fit" is a
       worse outcome than "your stretching space got smaller". */
    var areaSqft = (room.w * room.d) / 144;
    var reserveD = 0;
    if (areaSqft >= 250) reserveD = Math.min(96, room.d * 0.35);
    else if (areaSqft >= 100) reserveD = Math.min(60, room.d * 0.3);
    var reserve = reserveD > 0
      ? { x: 0, y: room.d - reserveD, w: room.w, d: reserveD }
      : null;

    var placed = [];
    var x = 0, y = 0, rowDepth = 0;
    var lastZone = items.length ? zoneOf(items[0].cls) : 0;

    /* The apron: a second packing cursor, just outside the back wall, for
       everything the room cannot hold. Started a little clear of the wall so the
       spill reads as separate from the gym rather than butted against it, and
       capped at one room-depth so an unfittable build cannot stretch the drawing
       until the room itself is unreadable. */
    var APRON_GAP = 12;
    var apronCap = Math.max(120, room.d);
    var apronFloor = room.d + APRON_GAP + apronCap;
    var ax = 0, ay = room.d + APRON_GAP, apronRow = 0, cascade = 0;

    /* Clearance is the gap BETWEEN machines, not a halo around each one.
       Padding both sides of every item double-counts every shared walkway: two
       treadmills back to back were reserving 60in of aisle where 30in is the
       whole requirement. That made a 20ft room look like it could hold three
       machines, and the builder trimmed away most of a perfectly good gym. Each
       item now carries half its clearance on each side, so neighbours share the
       space between them exactly as they do on a real floor. */
    items.forEach(function (it) {
      var fp = effFp(it);
      var pad = effPad(it);
      var w = fp.w + pad.x;
      var d = fp.d + pad.y;

      /* Break to a new row when the row is full OR when the band changes, so
         each function gets its own band across the floor. Uses the PROMOTED band
         (storage flankers ride in the strength band), not the raw class zone. */
      var z = it._band !== undefined ? it._band : zoneOf(it.cls);
      var zoneChanged = z !== lastZone;
      if ((x + w > room.w && x > 0) || (zoneChanged && x > 0)) {
        x = 0; y += rowDepth; rowDepth = 0;
      }
      lastZone = z;

      /* ── SAFETY-GOVERNED CLASSES NEVER TAKE THE FAST PATH ───────────────────
         A treadmill's 79in rear throw and a rack's 108in bar lane cannot be
         seen by a row cursor — it knows widths, not what lies beyond an edge.
         Both classes always go through the search, which checks those rules as
         hard constraints in auto layout. Everything else keeps the cheap path. */
      var safetyClass = it.cls === 'treadmill' || it.cls === 'rack';

      /* ── WHAT DOES NOT FIT GOES IN THE APRON, NOT OFF THE PAGE ─────────────
         This used to place every item on the same downward march regardless of
         whether the room had run out, so a build that overflowed kept being
         drawn further and further below the back wall. The canvas was only ever
         room-plus-margin tall, so those machines were clipped: in the tray, in
         the total, and invisible on the floor.

         Overflow is meant to be shown rather than prevented. So anything that
         will not fit is packed densely into an apron immediately outside the
         back wall — wrapping at the room's width, in reading order — where it
         is visible, flagged, and bounded. Eight overflowing machines now make
         one or two rows just outside the room instead of a column three room-
         depths long that nobody could see.

         Overflow items deliberately do NOT consume in-room space, so a wide
         piece that cannot fit no longer pushes the next machine out with it. */
      /* The cursor stops short of the open-floor reserve; the room itself has
         not run out — the rescue below may still use that space if it must. */
      var packDepth = reserve ? reserve.y : room.d;
      var fitsHere = !safetyClass && (y + d) <= packDepth && w <= room.w;

      if (fitsHere) {
        it.x = x + pad.x / 2;
        it.y = y + pad.y / 2;
        it.fits = true;
        it.tight = false;
        x += w;
        if (d > rowDepth) rowDepth = d;
        placed.push(it);
        return;
      }

      /* ── THE ROW CURSOR RAN OUT. THE ROOM MAY NOT HAVE. ────────────────────
         Before declaring this piece unfittable, ask the free-space search
         whether there is anywhere in the room it physically goes. The zone
         bands cost a row break each, so on a small floor the cursor is past the
         back wall long before the floor is full — which is exactly how a
         dumbbell set got flagged "will not fit" in a half-empty 11x12 room.

         Anything rescued here keeps the zone ORDER it was sorted into (so the
         tray still reads as a walk of the floor) but not the zone BAND, which is
         a nicety the room cannot always afford. */
      var inRoom = placed.filter(function (q) { return q.fits !== false; });
      var spot = null;

      if (opts.search !== false) {
        var step = { step: opts.step || 6, allowRotate: opts.allowRotate };
        var kos = opts.keepouts || [];

        /* Staged search, most respectful first:
             1. honour the open-floor reserve AND the safety rules;
             2. give up the reserve, keep safety;
             3. plain physical fit — but NOT for the safety classes. A treadmill
                with no legal rear throw belongs in the apron, visibly flagged,
                not squeezed somewhere its belt throws at a wall. That is this
                engine's version of the guideline's "reject with a reason". */
        if (reserve) {
          spot = findSpot(it, inRoom, room, kos.concat([reserve]),
                          { step: step.step, allowRotate: step.allowRotate, safety: true });
        }
        if (!spot) {
          spot = findSpot(it, inRoom, room, kos,
                          { step: step.step, allowRotate: step.allowRotate, safety: true });
        }
        if (!spot && !safetyClass) {
          spot = findSpot(it, inRoom, room, kos, step);
        }
      }

      if (spot) {
        it.x = spot.x;
        it.y = spot.y;
        it.rot = spot.rot;
        it.fits = true;
        it.tight = !!spot.tight;
        placed.push(it);
        return;
      }

      /* Genuinely will not fit. Show it in the apron, flagged. */
      it.tight = false;
      {
        if (ax + w > room.w && ax > 0) { ax = 0; ay += apronRow; apronRow = 0; }

        if (ay + d > apronFloor) {
          /* The apron is full too.

             It has to be bounded, or the problem simply moves: eight machines
             that are each wider than the room take eight apron rows, the canvas
             grows to eight room-depths, and the room is drawn as a sliver. A
             plan nobody can read is barely better than one that hides things.

             So the remainder cascade from the top of the apron in small steps.
             They overlap each other — every one of them is flagged as not
             fitting anyway, and the label on the drawing gives the count — but
             each stays visible and individually selectable, and the drawing
             stays at a scale where the room still means something. */
          it.x = Math.max(0, Math.min(room.w - fp.w, (cascade * 14) % Math.max(14, room.w * 0.5)));
          it.y = room.d + APRON_GAP + (cascade * 14) % Math.max(14, apronCap * 0.5);
          cascade++;
        } else {
          it.x = ax + pad.x / 2;
          it.y = ay + pad.y / 2;
          ax += w;
          if (d > apronRow) apronRow = d;
        }
        it.fits = false;
      }

      placed.push(it);
    });

    return { placed: placed, usedDepth: y + rowDepth };
  }

  function roomArea(room) { return (room.w / 12) * (room.d / 12); }

  /* ── 3b. BUDGET CURVES ─────────────────────────────────────────────────────
     A linear slider is the wrong instrument for this catalogue. Spanning
     $2,500 to $250,000 linearly puts every residential gym in the first 5% of
     travel, so the useful range is a few pixels wide and the midpoint means
     nothing. These curves put the typical build near the middle of the handle's
     journey and reserve the last quarter of travel for the top half of the
     money, which is where the outliers live.

     Values are in CENTS throughout, matching STATE.budget and every price in
     the catalogue. Positions are 0..1.

     Kept here rather than in the UI file, and expressed as a table rather than
     as conditionals, so both directions of the mapping can be tested outside a
     browser. */
  var ROOM_TIER = {
    'spare-room': 'residential',
    'one-car':    'residential',
    'two-car':    'residential',
    'basement':   'residential',
    /* Studio is not named in the spec. It is a 1,500 sq ft facility — roughly
       ten times a spare bedroom — and capping it at the residential $75,000
       would put a fully equipped floor out of reach of its own slider. It rides
       the commercial curve. */
    'studio':     'commercial',
    'commercial': 'commercial',
    'custom':     'commercial'
  };

  var BUDGET_CURVES = {
    residential: [[0, 250000], [0.25, 750000], [0.5, 1250000], [0.75, 3000000], [1, 7500000]],
    commercial:  [[0, 1000000], [0.25, 5000000], [0.5, 8000000], [0.75, 12500000], [1, 25000000]]
  };

  function budgetTier(preset) {
    return ROOM_TIER[preset] || 'residential';
  }

  function budgetCurve(preset) {
    return BUDGET_CURVES[budgetTier(preset)];
  }

  function budgetMin(preset) { var c = budgetCurve(preset); return c[0][1]; }
  function budgetMax(preset) { var c = budgetCurve(preset); return c[c.length - 1][1]; }

  /* Displayed money should move in increments someone would actually say out
     loud. Dollar-by-dollar tracking on a $250,000 range is noise. */
  function roundBudget(cents) {
    var step = cents < 2000000 ? 50000        /* under $20k → $500 */
             : cents < 10000000 ? 100000      /* under $100k → $1,000 */
             : 500000;                        /* above → $5,000 */
    return Math.round(cents / step) * step;
  }

  /* Slider position → money. Linear between anchors, so the curve is monotonic
     and every anchor lands exactly on its stated value. */
  function budgetForPos(pos, preset) {
    var c = budgetCurve(preset);
    var p = Math.max(0, Math.min(1, Number(pos) || 0));

    for (var i = 0; i < c.length - 1; i++) {
      var a = c[i], b = c[i + 1];
      if (p >= a[0] && p <= b[0]) {
        var span = b[0] - a[0];
        var t = span ? (p - a[0]) / span : 0;
        return roundBudget(a[1] + (b[1] - a[1]) * t);
      }
    }
    return c[c.length - 1][1];
  }

  /* Money → slider position. The inverse of the above, so a saved budget puts
     the handle back where it was rather than at a position computed from a
     different formula that drifts. */
  function posForBudget(cents, preset) {
    var c = budgetCurve(preset);
    var v = clampBudget(cents, preset);

    for (var i = 0; i < c.length - 1; i++) {
      var a = c[i], b = c[i + 1];
      if (v >= a[1] && v <= b[1]) {
        var span = b[1] - a[1];
        var t = span ? (v - a[1]) / span : 0;
        return a[0] + (b[0] - a[0]) * t;
      }
    }
    return v <= c[0][1] ? 0 : 1;
  }

  /* Legacy and out-of-range values land inside the current room type's range
     rather than being discarded. A saved $180,000 commercial build that is
     re-opened as a spare bedroom becomes $75,000, not zero. */
  function clampBudget(cents, preset) {
    var n = Number(cents);
    if (!isFinite(n) || n <= 0) return budgetForPos(0.5, preset);
    return Math.max(budgetMin(preset), Math.min(budgetMax(preset), n));
  }

  /* ── 3c. DOES THIS LAYOUT ACTUALLY FIT? ────────────────────────────────────
     Answered from the real geometry, never from summed square footage. Area
     says a 20ft room holds a 19ft rack and a 19ft rack; the floor disagrees.

     Two independent failures are reported separately because they need
     different words in front of a shopper: a machine can be outside the walls,
     or it can be sitting on top of another one.

     `keepouts` is optional — the caller passes door swings and columns when it
     has them. Nothing here invents a safety clearance that the builder does not
     already model. */
  function fitReport(items, room, keepouts) {
    var outside = [], overlapping = [], blocked = [];
    var i, j;

    for (i = 0; i < items.length; i++) {
      var e = effFp(items[i]);
      var x = items[i].x || 0, y = items[i].y || 0;
      /* A hair of tolerance: positions are floats and a machine placed flush to
         a wall can land at 720.0000000001. */
      if (x < -0.01 || y < -0.01 || x + e.w > room.w + 0.01 || y + e.d > room.d + 0.01) {
        outside.push(i);
      }
    }

    for (i = 0; i < items.length; i++) {
      for (j = i + 1; j < items.length; j++) {
        var ea = effFp(items[i]), eb = effFp(items[j]);
        if (items[i].x < items[j].x + eb.w && items[i].x + ea.w > items[j].x &&
            items[i].y < items[j].y + eb.d && items[i].y + ea.d > items[j].y) {
          if (overlapping.indexOf(i) === -1) overlapping.push(i);
          if (overlapping.indexOf(j) === -1) overlapping.push(j);
        }
      }
    }

    if (keepouts && keepouts.length) {
      for (i = 0; i < items.length; i++) {
        var ei = effFp(items[i]);
        for (j = 0; j < keepouts.length; j++) {
          var k = keepouts[j];
          if (!k) continue;
          if (items[i].x < k.x + k.w && items[i].x + ei.w > k.x &&
              items[i].y < k.y + k.d && items[i].y + ei.d > k.y) {
            if (blocked.indexOf(i) === -1) blocked.push(i);
            break;
          }
        }
      }
    }

    var bad = {};
    outside.concat(overlapping, blocked).forEach(function (n) { bad[n] = true; });

    return {
      ok: outside.length === 0 && overlapping.length === 0 && blocked.length === 0,
      outside: outside,
      overlapping: overlapping,
      blocked: blocked,
      bad: Object.keys(bad).map(Number)
    };
  }

  /* Would this set of items fit the proposed room if we were allowed to
     re-arrange them? Used to answer "is the room too small" rather than "is the
     current arrangement wrong" — a shopper shrinking a room has not asked us to
     preserve their exact placement, only to keep their equipment.

     Works on COPIES. `arrange` writes coordinates onto whatever it is handed,
     and running this on the live items would silently re-lay the floor — a bug
     this codebase has already shipped once. */
  function couldFit(items, room) {
    var probe = items.map(function (it) {
      return { p: it.p, cls: it.cls, fp: it.fp, x: 0, y: 0, rot: it.rot || 0 };
    });
    arrange(probe, room);
    return fitReport(probe, room).ok;
  }

  /* ── 4. RECOMMENDATION ─────────────────────────────────────────────────────
     Dillon's rules, verbatim:
       · cardio  — highest-dollar machine that fits first, then more only if
                   space and budget allow
       · strength — NEW first, remanufactured only when budget requires it
       · budget  — a soft guide. It shapes the opening build and never blocks.
       · margin  — explicitly dropped. "Just use the right products for the user." */

  /* Dillon, 2026-08-05: "don't use any 'bundle' products. anything with bundle
     in the name, disregard."

     Bundles are pre-packed multi-item SKUs. In a builder they double-count — the
     shopper places a rack, then a "rack + bench + barbell" bundle lands beside
     it — and their footprint is meaningless because it describes several
     machines at once. Excluded from recommendation AND from the swap panel, so
     there is no route by which one reaches a plan. */
  function eligible(p) {
    if (/bundle/i.test(p.t || '')) return false;
    if (/bundle/i.test(p.ty || '')) return false;
    return true;
  }

  /* Brand ranking. Dillon: "heavily prioritize built over york products."
     Applied as a sort key rather than a filter — York still appears if nothing
     else fills a slot, it just never wins a tie. */
  function brandRank(p) {
    var t = String(p.t || '');
    if (/BUILT/i.test(t)) return 0;
    if (/york/i.test(t)) return 2;
    return 1;
  }

  /* Storage and free weights sit at the END of every list on purpose: a gym is
     machines first, and racks, plates and bars fill in around them once the real
     equipment is placed. Being in the list at all is what makes them eligible —
     anything absent sorts to 99 and effectively never gets picked. */
  var GOAL_ORDER = {
    strength:   ['rack', 'bench', 'multi', 'plate', 'selector', 'storage', 'freeweight'],
    muscle:     ['multi', 'selector', 'plate', 'bench', 'rack', 'storage', 'freeweight'],
    cardio:     ['treadmill', 'elliptical', 'bike', 'rower', 'stepper', 'freeweight'],
    general:    ['multi', 'treadmill', 'rack', 'bench', 'selector', 'storage', 'freeweight'],
    commercial: ['multi', 'selector', 'treadmill', 'elliptical', 'plate', 'rack', 'bench', 'storage', 'freeweight']
  };

  function recommend(catalog, opts) {
    var room = opts.room;
    var budget = opts.budget;              /* cents, 0 = no limit */
    var goal = opts.goal || 'general';
    var cardioBias = opts.cardioBias || 0.35;

    /* Free weights are placed on the floor now, same as anything else — Dillon,
       2026-08-05. They were tray-only when nothing could draw them; every free
       weight type has real top-down art now, and a rack of dumbbells genuinely
       does occupy floor a machine cannot.

       They stay OUT of the PDP's "see this in my gym" button, which is a
       separate decision and lives in ctx-see-in-my-gym.liquid. */
    var pool = catalog.filter(eligible).map(function (p) {
      return { p: p, cls: classify(p), fp: footprint(p) };
    });

    var area = roomArea(room);
    var picks = [];
    var spend = 0;

    function affordable(it) { return !budget || spend + it.p.p <= budget * 1.05; }

    /* THE FIT TEST IS THE PACKER ITSELF.
       An earlier version budgeted floor space by summing raw footprint AREA and
       capping it at a fraction of the room. That silently disagreed with how the
       room is actually laid out — rows consume depth in whole bands, and every
       machine carries clearance padding — so the selector kept choosing sets the
       packer could not place, and machines rendered outside the walls. Asking
       the real packer whether a candidate fits is slower and always honest. */
    function accepts(candidate) {
      var trial = picks.concat([candidate]);
      arrange(trial, room);
      for (var i = 0; i < trial.length; i++) if (trial[i].fits === false) return false;
      return true;
    }

    /* --- cardio: one flagship, most expensive that fits ------------------- */
    var cardio = pool.filter(function (it) { return CLASSES[it.cls].group === 'cardio'; })
                     .sort(function (a, b) {
                       var ar = brandRank(a.p), br = brandRank(b.p);
                       if (ar !== br) return ar - br;
                       return b.p.p - a.p.p;
                     });

    /* Conditioning-led builds spend more of the budget on the flagship, because
       the flagship IS the gym for that shopper rather than one corner of it. */
    if (goal === 'cardio') cardioBias = Math.max(cardioBias, 0.55);
    var cardioBudget = budget ? budget * cardioBias : Infinity;
    var affordableCardio = cardio.filter(function (it) { return it.p.p <= cardioBudget; });
    var ladder = affordableCardio.length ? affordableCardio : cardio.slice().reverse();
    var flagshipCost = 0;
    for (var f = 0; f < ladder.length; f++) {
      if (accepts(ladder[f])) {
        picks.push(ladder[f]);
        spend += ladder[f].p.p;
        flagshipCost = ladder[f].p.p;
        break;
      }
    }

    /* --- strength: new before reman -------------------------------------- */
    var order = GOAL_ORDER[goal] || GOAL_ORDER.general;

    function strengthPool(condition) {
      return pool
        .filter(function (it) {
          /* Storage and free weights ride with strength rather than being a pool
             of their own — they compete for the same floor and the same budget,
             and GOAL_ORDER already ranks them behind the machines. Excluding
             them here is what previously made weight racks and dumbbells
             impossible to recommend at all. */
          var grp = CLASSES[it.cls].group;
          var eligibleGroup = grp === 'strength' || grp === 'storage' || grp === 'accessory';
          return eligibleGroup && it.p.cn === condition;
        })
        .sort(function (a, b) {
          var ai = order.indexOf(a.cls), bi = order.indexOf(b.cls);
          if (ai < 0) ai = 99; if (bi < 0) bi = 99;
          if (ai !== bi) return ai - bi;

          /* BUILT ahead of York, both ahead of nothing — brand outranks price so
             the recommendation leads with the house line rather than whatever
             happens to be most expensive. */
          var ar = brandRank(a.p), br = brandRank(b.p);
          if (ar !== br) return ar - br;

          return b.p.p - a.p.p;
        });
    }

    var seq = strengthPool('new').concat(strengthPool('reman'));

    var cardioGoal = goal === 'cardio';
    /* A real gym floor has a bank of cardio, not a token machine. Scaled to the
       room the same way the strength variety cap is: 5 per class in a big space,
       2 in a garage, 4 when conditioning is the stated goal. */
    var cardioCap = cardioGoal ? 4 : (area > 800 ? 5 : 2);
    var cardioFloor = cardioGoal ? 120 : 420;

    /* Pulled out of the tail of this function so it can run BEFORE strength when
       conditioning is the goal. Left where it was, the strength pass spent the
       budget first and a conditioning-led build came back with one bike — the
       shopper's stated priority losing to whatever ran earlier in the code. */
    /* Cardio needs a CEILING as well as a floor. Running it first fixed the
       "one lonely bike" problem and immediately created the opposite one: a
       1500 sq ft studio came back 16 machines, every one of them cardio,
       because a per-class cap of 5 across ten cardio classes is no cap at all
       and the pass simply spent the whole budget before strength ever ran.

       Two limits, both proportional rather than absolute:
         · a share of the BUDGET, so cardio can never crowd out the strength floor
         · a count scaled to ROOM SIZE, roughly one machine per 500 sq ft, which
           is about the density a real gym floor runs at */
    var cardioSpend = flagshipCost;
    var cardioTotalCap = cardioGoal ? 6 : Math.max(1, Math.floor(area / 500));
    var cardioSpendCap = budget ? budget * (cardioGoal ? 0.70 : 0.38) : Infinity;

    function cardioCount() {
      return picks.filter(function (it) { return CLASSES[it.cls].group === 'cardio'; }).length;
    }

    function fillCardio() {
      for (var c = 0; c < cardio.length && area > cardioFloor; c++) {
        var cd = cardio[c];
        if (seen[cd.p.h]) continue;
        if (cardioCount() >= cardioTotalCap) break;
        if (cardioSpend + cd.p.p > cardioSpendCap) continue;
        if (!affordable(cd)) continue;
        if ((classCount[cd.cls] || 0) >= cardioCap) continue;
        if (!accepts(cd)) continue;
        picks.push(cd); seen[cd.p.h] = true; spend += cd.p.p; cardioSpend += cd.p.p;
        classCount[cd.cls] = (classCount[cd.cls] || 0) + 1;
      }
    }

    var seen = {};
    picks.forEach(function (it) { seen[it.p.h] = true; });
    var classCount = {};
    picks.forEach(function (it) { classCount[it.cls] = 1; });

    /* Big rooms claim their cardio before strength spends the budget, for the
       same reason the conditioning goal does: on a commercial floor cardio is a
       whole zone, and leaving it until last meant a 5000 sq ft build came back
       with a single bike. */
    if (cardioGoal || area > 800) fillCardio();

    for (var i = 0; i < seq.length; i++) {
      var it = seq[i];
      if (seen[it.p.h]) continue;

      if (!affordable(it)) continue;

      /* Variety: no more than 2 of a class in a home room, 5 in a big one. */
      var cap = area > 800 ? 5 : 2;
      if ((classCount[it.cls] || 0) >= cap) continue;

      if (!accepts(it)) continue;

      picks.push(it);
      seen[it.p.h] = true;
      spend += it.p.p;
      classCount[it.cls] = (classCount[it.cls] || 0) + 1;
    }

    /* And a final sweep with whatever budget survived. `seen` makes this safe to
       run twice — a big room simply gets first refusal above. */
    fillCardio();

    return picks;
  }

  /* Alternatives for the swap panel: same class, ranked by closeness in price
     so the first suggestions feel like lateral moves rather than upsells. */
  function alternatives(catalog, item, limit) {
    var cls = item.cls;
    return catalog
      .filter(function (p) { return p.h !== item.p.h && eligible(p) && classify(p) === cls; })
      .sort(function (a, b) {
        var ar = brandRank(a), br = brandRank(b);
        if (ar !== br) return ar - br;
        return Math.abs(a.p - item.p.p) - Math.abs(b.p - item.p.p);
      })
      .slice(0, limit || 8)
      .map(function (p) { return { p: p, cls: classify(p), fp: footprint(p) }; });
  }

  /* ── COVERAGE ──────────────────────────────────────────────────────────────
     What this gym actually trains. The point is persuasion: a $20k total reads
     as "a lot of money" on its own, and as "everything I train" next to a
     filled-in coverage bar. Gaps then sell the next machine as advice rather
     than as an upsell — "nothing here trains legs" is a fact the shopper can
     check against their own goals.

     Scored off custom.product_type, which names the muscle group directly for
     most of the catalogue ("Legs Selectorized", "Back Plate-Loaded"). */
  var AREAS = [
    { key: 'push',    label: 'Push' },
    { key: 'pull',    label: 'Pull' },
    { key: 'legs',    label: 'Legs' },
    { key: 'core',    label: 'Core' },
    { key: 'cardio',  label: 'Conditioning' }
  ];

  function areasFor(item) {
    var s = ((item.p.ty || '') + ' ' + (item.p.t || '')).toLowerCase();
    var cls = item.cls;
    var group = CLASSES[cls] ? CLASSES[cls].group : 'strength';
    var out = [];

    if (group === 'cardio') return ['cardio'];

    /* ── WORD BOUNDARIES ARE LOAD-BEARING HERE ──────────────────────────────
       These were bare substrings, and on this catalogue that is not a subtlety —
       it was wrong on common products:

         · `lat`  matched "F-LAT- Bench" and "P-LAT-e Tree", so a flat bench and
                  a plate tree were both credited with upper-body PULLING.
         · `ab`   matched "-Ab-justable"… i.e. "adjust-ABLE" — so every adjustable
                  bench and every c-ABLE machine was credited with CORE.
         · `row`  would match "TH-ROW-BACK", and BUILT has a Throwback Series.
         · `back` does NOT match inside "throwback" (no boundary before the b),
                  which is why that one never showed up.

       The coverage bars are a persuasion surface — "nothing here trains legs" is
       meant to be a fact the shopper can check — so a bench that claims to train
       pull discredits the whole read. The new suggestion reasons are what finally
       made it visible: a flat bench was being offered as "completes upper-body
       pulling coverage".

       `\blats?\b` deliberately does not match "lateral": lateral raises are a
       shoulder movement and belong to push, which the `shoulder` term catches. */
    /* ── LEGS WINS ITS OWN WORDS BACK ───────────────────────────────────────
       Measured 2026-08-07 across the live catalogue, three more of exactly the
       family of bug the word boundaries above were added to fix:

         · `\bcurls?\b` credited LEG CURLS as upper-body PULL — 15 of the 59
           machines whose type says Legs.
         · `\bpress\b`  credited LEG PRESSES as PUSH — 9 of those same 59.
         · `\bback\b`   credited AB machines as PULL — 2 of 9, both of them
           "… / Lower Back" models.

       A word boundary is not enough when the word is genuinely shared between
       two muscle groups. The qualifier has to be read as well: a leg curl is a
       curl, but it is not a bicep curl. So the leg terms are tested FIRST and,
       where they hit, the arm/chest reading of the same word is suppressed. */
    var legWords = /\blegs?\b|squat|\bcalf|glute|hamstring|\bquads?\b|\bhack\b|lunge|\bthigh\b|abduct|adduct/;
    var isLeg = legWords.test(s);
    var isAb  = /\babs?\b|abdominal|\bcore\b|crunch|oblique|hyperextension|knee raise|\bvkr\b/.test(s);

    if (/chest|shoulder|\bdips?\b|\bfl(y|ies)\b|tricep/.test(s)) out.push('push');
    else if (/\bpress\b/.test(s) && !isLeg) out.push('push');

    if (/\brow(s|ing)?\b|pulldown|pull-?up|bicep|\blats?\b/.test(s)) out.push('pull');
    else if (/\bcurls?\b/.test(s) && !isLeg) out.push('pull');
    else if (/\bback\b/.test(s) && !isAb) out.push('pull');

    if (isLeg) out.push('legs');
    if (isAb) out.push('core');

    /* Anything you can load a barbell on covers the compound lifts, which is
       most of what a home gym is actually for.

       `allinone` WAS DEAD. classify() never returns it — it returns multi4 /
       multi5 / multi8 — so all seven multi-station rigs in the catalogue, the
       most complete strength machines on the store, fell straight through to
       the fallback and were credited with pushing only. The alias exists in the
       model library (MODELS.allinone = MODELS.multi5), which is where the name
       came from; the classifier never used it. */
    if (cls === 'rack' || cls === 'smith' || cls === 'functional' ||
        cls === 'multi4' || cls === 'multi5' || cls === 'multi8') {
      out.push('push', 'pull', 'legs');
    }
    if (cls === 'benchflat' || cls === 'benchspec' || cls === 'benchadj') out.push('push');
    if (cls === 'benchab') out.push('core');

    if (out.length) return out;

    /* ── THE FALLBACK MUST NOT CREDIT STORAGE WITH A MUSCLE GROUP ────────────
       Anything unmatched used to fall through to ['push'], which is a reasonable
       guess for an unrecognised MACHINE and plainly wrong for a plate tree, a
       dumbbell rack or a set of bands: a weight tree was filling in the Push bar
       while training nothing at all.

       That matters because the bar is an argument. "Nothing here trains legs" is
       only persuasive if the filled-in halves are honest, and a rack of plates
       counted as pushing meant a gym with no pressing station could still read as
       covered. Storage and accessories now contribute nothing to coverage, which
       is the truth — they are what you put the weight on, not what you train
       with. They still appear on the plan, in the tray and in the total. */
    var grp = CLASSES[cls] ? CLASSES[cls].group : 'strength';
    if (grp === 'storage' || grp === 'accessory') return [];

    return ['push'];
  }

  function coverage(items) {
    var score = {};
    AREAS.forEach(function (a) { score[a.key] = 0; });

    items.forEach(function (it) {
      areasFor(it).forEach(function (k) { if (score[k] != null) score[k] += 1; });
    });

    /* Two dedicated machines per area is a complete gym for that area. More is
       depth, not coverage, so the bar caps rather than rewarding padding. */
    return AREAS.map(function (a) {
      return { key: a.key, label: a.label, pct: Math.min(100, Math.round(score[a.key] / 2 * 100)), n: score[a.key] };
    });
  }

  /* ── SUGGESTIONS: COVERAGE FIRST, THEN THE LEFTOVERS ───────────────────────
     `recommend` builds a whole gym from an empty room. It is the right function
     for the opening build and the wrong one for "what next" — which is what the
     suggestions panel was using it for, by generating a complete gym from scratch
     and subtracting whatever was already on the floor.

     Three things went wrong with that. It never considered the equipment already
     placed except to filter it out, so it could not tell a gym with no leg
     machine from one with three. It ran out after five or six picks, which is why
     the panel said "your gym already covers our suggestions" on a two-machine
     build in a half-empty room. And it never asked whether a suggestion could
     actually be placed, so the panel offered one-click Adds that immediately
     failed.

     This is the other function. It reads the floor as it stands and answers in
     two phases, which is the order the brief asks for and also the order a
     salesperson would use:

       Phase 1 — make the gym COMPLETE. Fill the movement patterns that nothing
                 currently trains, in an order that follows the stated goal.
       Phase 2 — spend what is left. Once the gym works, use the remaining budget
                 and floor on BUILT products that add depth.

     Every suggestion carries a REASON, and a measured `fits` from the real
     placement search against the real floor. Nothing is offered as a one-click
     Add when the engine already knows it has nowhere to go — it is offered with
     the truth attached instead. */

  /* Which gaps matter most, per stated goal. Cardio last for lifters, first for
     a conditioning build or a commercial floor where it is a whole zone. */
  var COVER_ORDER = {
    strength:   ['legs', 'push', 'pull', 'core', 'cardio'],
    muscle:     ['push', 'pull', 'legs', 'core', 'cardio'],
    cardio:     ['cardio', 'legs', 'push', 'pull', 'core'],
    general:    ['push', 'pull', 'legs', 'cardio', 'core'],
    commercial: ['cardio', 'push', 'pull', 'legs', 'core']
  };

  var AREA_LABEL = {
    push: 'upper-body pushing', pull: 'upper-body pulling',
    legs: 'lower-body', core: 'core', cardio: 'conditioning'
  };

  function suggest(catalog, opts) {
    opts = opts || {};
    var room = opts.room;
    var have = opts.have || [];
    var budget = opts.budget || 0;
    var goal = opts.goal || 'general';
    var keepouts = opts.keepouts || [];
    var limit = opts.limit || 8;

    var spent = have.reduce(function (a, it) { return a + (it.p.p || 0); }, 0);
    /* The same 5% tolerance `recommend` uses. A suggestion a few dollars over the
       slider is not a mistake; the budget is a guide. */
    var left = budget ? (budget * 1.05 - spent) : Infinity;

    /* Already on the floor, by VARIANT — two dumbbell sets that differ only in
       weight range are different objects and must not pool. */
    var owned = {};
    have.forEach(function (it) { owned[it.p.v || it.p.h] = true; });

    var pool = catalog
      .filter(eligible)
      .filter(function (p) { return !owned[p.v || p.h]; })
      .map(function (p) { return { p: p, cls: classify(p), fp: footprint(p) }; });

    var cov = {};
    coverage(have).forEach(function (a) { cov[a.key] = a.n; });

    var out = [];
    var taken = {};

    /* ── THE SEARCH HAS A BUDGET, AND IT NEEDS ONE ─────────────────────────────
       Measured 2026-08-06: one `suggest()` call took 6ms on an empty room and
       **49.6ms on a full one, on a desktop**. On a mid-range phone that is a
       quarter to half a second, and it runs on every single Add — which is most
       of why the equipment panel felt like it froze.

       Two things were wrong. Every candidate was probed TWICE (once to decide
       whether to offer it, once inside `push` to record `fits`), and Phase 2 kept
       probing the entire BUILT catalogue when the room was full, because it only
       stopped once it had found `limit` products that fitted and on a full floor
       it never found any.

       So: results are memoised per product, and the number of searches is capped.
       When the cap is hit the remaining rows are still offered — ranked and
       labelled honestly as unprobed rather than silently claimed to fit, which
       matters because the Add button's wording depends on it. */
    var PROBE_BUDGET = 90;
    var probesUsed = 0;
    var probeCache = {};

    function fitOf(cand) {
      var key = cand.p.h;
      if (probeCache[key]) return probeCache[key];

      if (probesUsed >= PROBE_BUDGET) {
        /* Unknown, not "fits". `fits: true` here would put "Add to gym" on a row
           that cannot be placed; `fits: false` would put "No room yet" on one
           that can. Neither is honest, so the caller treats null as "just offer
           it normally and let the Add action find out". */
        return { fits: null, tight: false };
      }

      probesUsed++;
      /* safety: true — a suggestion is an auto placement, and the guideline's
         hard rules apply: a treadmill is not offered as "fits" in a room that
         cannot give its rear throw 79 real inches, and a rack is not offered
         where no 108in bar lane exists. The honest "No room yet" label handles
         the rest. */
      var spot = findSpot(cand, have, room, keepouts, { step: 12, safety: true });
      probeCache[key] = { fits: !!spot, tight: !!(spot && spot.tight) };
      return probeCache[key];
    }

    function rank(a, b) {
      var ar = brandRank(a.p), br = brandRank(b.p);
      if (ar !== br) return ar - br;              /* BUILT first, York last */
      return (b.p.p || 0) - (a.p.p || 0);         /* then the better machine */
    }

    function push(cand, reason) {
      if (out.length >= limit) return false;
      if (taken[cand.p.h]) return false;
      var f = fitOf(cand);
      taken[cand.p.h] = true;
      out.push({
        p: cand.p, cls: cand.cls, fp: cand.fp,
        reason: reason, fits: f.fits, tight: f.tight,
        /* Affordability is reported, never used to hide a product. Someone at
           their budget ceiling is still allowed to see the next machine. */
        overBudget: left !== Infinity && cand.p.p > left
      });
      return true;
    }

    /* Candidates that train a given area, best first, affordable first, and
       PLACEABLE first — a suggestion that fits is more useful than a marginally
       better one that does not. */
    function forArea(area) {
      return pool
        .filter(function (c) { return !taken[c.p.h] && areasFor(c).indexOf(area) > -1; })
        .sort(function (a, b) {
          var aa = (left === Infinity || a.p.p <= left) ? 0 : 1;
          var ba = (left === Infinity || b.p.p <= left) ? 0 : 1;
          if (aa !== ba) return aa - ba;
          return rank(a, b);
        });
    }

    var order = COVER_ORDER[goal] || COVER_ORDER.general;

    /* ── Phase 1a: areas nothing trains at all ─────────────────────────────── */
    /* Bounded on purpose. Scanning a whole class for one that fits is fine;
       scanning 300 of them per area is what made this slow. Twelve candidates is
       several screens' worth of alternatives and the list is already ranked. */
    var SCAN_PER_AREA = 12;

    order.forEach(function (area) {
      if (out.length >= limit) return;
      if ((cov[area] || 0) > 0) return;
      var list = forArea(area);
      var scan = Math.min(list.length, SCAN_PER_AREA);
      for (var i = 0; i < scan; i++) {
        /* Prefer one that fits, but do not leave a genuine coverage hole
           unmentioned just because the room is currently full — that is what the
           honest label is for. `fits !== false` also accepts an unprobed row once
           the budget is spent. */
        if (fitOf(list[i]).fits !== false || i === scan - 1) {
          if (push(list[i], 'Completes ' + (AREA_LABEL[area] || area) + ' coverage')) break;
        }
      }
    });

    /* ── Phase 1b: areas with only one machine ─────────────────────────────── */
    order.forEach(function (area) {
      if (out.length >= limit) return;
      if ((cov[area] || 0) !== 1) return;
      var list = forArea(area);
      var scan = Math.min(list.length, SCAN_PER_AREA);
      for (var i = 0; i < scan; i++) {
        if (fitOf(list[i]).fits !== false) {
          push(list[i], 'Adds depth to ' + (AREA_LABEL[area] || area) + ' training');
          break;
        }
      }
    });

    /* ── Phase 2: the gym works — now spend what is left ───────────────────────
       BUILT only, because this is the discretionary half of the budget and the
       house line is what Dillon wants it spent on. Storage and free weights are
       welcome here: once the machines are in, a rack of dumbbells and somewhere
       to put the plates is what finishes a room. */
    var extras = pool
      .filter(function (c) { return !taken[c.p.h] && brandRank(c.p) === 0; })
      .filter(function (c) { return left === Infinity || c.p.p <= left; })
      .sort(rank);

    /* Bounded scan, for the reason above: on a full floor this loop used to probe
       every BUILT product in the catalogue and find nothing, which is the single
       most expensive thing the panel did. */
    var scanned = 0;
    for (var e = 0; e < extras.length && out.length < limit && scanned < 40; e++) {
      scanned++;
      if (fitOf(extras[e]).fits === false) continue;
      push(extras[e], 'Fits your remaining space and budget');
    }

    /* Still short — the room is full, or the budget is. Say which, with products
       that would be next if it were not. Ranked last, and labelled. */
    if (out.length < limit) {
      var rest = pool.filter(function (c) { return !taken[c.p.h]; }).sort(rank);
      for (var r = 0; r < rest.length && out.length < limit; r++) push(rest[r], '');
    }

    /* Anything placeable outranks anything that is not, whatever its reason —
       the panel's top rows must be things a single tap actually achieves. */
    /* Known-fits first, unprobed second, known-not-fits last. `fits` is a
       THREE-state value now — true / null / false — so a plain truthiness test
       would file every unprobed row in with the ones that definitely do not fit
       and bury them at the bottom. */
    function fitRank(v) { return v === true ? 0 : (v === false ? 2 : 1); }

    out.sort(function (a, b) {
      var fa = fitRank(a.fits), fb = fitRank(b.fits);
      if (fa !== fb) return fa - fb;
      if (!!a.overBudget !== !!b.overBudget) return a.overBudget ? 1 : -1;
      return 0;
    });

    return out;
  }

  window.CTXGymStudio = {
    loadCatalog: loadCatalog,
    classify: classify,
    footprint: footprint,
    arrange: arrange,
    recommend: recommend,
    suggest: suggest,
    alternatives: alternatives,
    areasFor: areasFor,
    tierFor: tierFor,
    nextTier: nextTier,
    CLASSES: CLASSES,
    TRAY_ONLY: TRAY_ONLY,
    roomArea: roomArea,
    budgetTier: budgetTier,
    budgetMin: budgetMin,
    budgetMax: budgetMax,
    budgetForPos: budgetForPos,
    posForBudget: posForBudget,
    clampBudget: clampBudget,
    roundBudget: roundBudget,
    ROOM_TIER: ROOM_TIER,
    BUDGET_CURVES: BUDGET_CURVES,
    fitReport: fitReport,
    couldFit: couldFit,
    findSpot: findSpot,
    clearanceReport: clearanceReport,
    overlapPairs: overlapPairs,
    tightPairs: tightPairs,
    placementStates: placementStates,
    envPad: envPad,
    envFor: function (cls) { return ENV[cls] || ENV_DEFAULT; },
    rearZone: rearZone,
    occupied: occupied,
    effFp: effFp,
    effPad: effPad,
    turned: turned,
    eligible: eligible,
    brandRank: brandRank,
    coverage: coverage
  };
})();
